import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WatchlistProvider } from "@/context/WatchlistContext";
import { HoldingsProvider } from "@/context/HoldingsContext";
import { ChainProvider } from "@/context/ChainContext";
import { AuthProvider } from "@/context/AuthContext";
import { AgeFilterProvider } from "@/context/AgeFilterContext";
import { AuthModal } from "@/components/AuthModal";
import { WelcomeTour } from "@/components/WelcomeTour";
import { TipBanner } from "@/components/TipBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatus } from "@/components/NetworkStatus";
import { Sidebar } from "@/components/Sidebar";
import { FreshDropWatcher } from "@/components/FreshDropWatcher";
import { Tier3Watcher } from "@/components/Tier3Badge";
import Dashboard from "@/pages/Dashboard";
import CoinDetail from "@/pages/CoinDetail";
import Watchlist from "@/pages/Watchlist";
import MyWallet from "@/pages/MyWallet";
import WalletBuys from "@/pages/WalletBuys";
import Launches from "@/pages/Launches";
import Devs from "@/pages/Devs";
import LaunchHub from "@/pages/LaunchHub";
import FreshDrops from "@/pages/FreshDrops";
import WalletScout from "@/pages/WalletScout";
import Screener from "@/pages/Screener";
import WaitingRoom from "@/pages/WaitingRoom";
import Confirmation from "@/pages/Confirmation";
import FundingWeb from "@/pages/FundingWeb";
import Methodology from "@/pages/Methodology";
import Alerts from "@/pages/Alerts";
import SmartMoney from "@/pages/SmartMoney";
import ClusterBuys from "@/pages/ClusterBuys";
import CabalRadar from "@/pages/CabalRadar";
import CabalProfile from "@/pages/CabalProfile";
import MillyBoard from "@/pages/MillyBoard";
import CtoRadar from "@/pages/CtoRadar";
import OrganicRunners from "@/pages/OrganicRunners";
import CandleRadar from "@/pages/CandleRadar";
import RobinhoodFeed from "@/pages/RobinhoodFeed";
import WatchlistRunners from "@/pages/WatchlistRunners";
import Key from "@/pages/Key";
import Leaderboard from "@/pages/Leaderboard";
import Newsroom from "@/pages/Newsroom";
import HandleRadar from "@/pages/HandleRadar";
import MyBuilders from "@/pages/MyBuilders";
import ProvenDevs from "@/pages/ProvenDevs";
import SurvivorWatch from "@/pages/SurvivorWatch";
import GithubRadar from "@/pages/GithubRadar";
import MorningCall from "@/pages/MorningCall";
import SignalScanner from "@/pages/SignalScanner";
import MomentumRadar from "@/pages/MomentumRadar";
import Trending from "@/pages/Trending";
import Admin from "@/pages/Admin";
import Disclaimer from "@/pages/Disclaimer";
import { RouteTracker } from "@/components/RouteTracker";
import { ReportIssueWidget } from "@/components/ReportIssueWidget";

function App() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("aa_sidebar_collapsed") === "1");
  const toggleCollapsed = (updater) => {
    setCollapsed((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("aa_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };
  return (
    <div className="App min-h-screen bg-background grid-bg">
      <AuthProvider>
      <WatchlistProvider>
        <HoldingsProvider>
        <ChainProvider>
        <AgeFilterProvider>
        <TooltipProvider delayDuration={150} skipDelayDuration={300}>
        <BrowserRouter>
          <Sidebar collapsed={collapsed} setCollapsed={toggleCollapsed} />
          <AuthModal />
          <WelcomeTour />
          <RouteTracker />
          <FreshDropWatcher />
          <Tier3Watcher />
          <div className={`min-h-screen transition-[padding] duration-200 ${collapsed ? "lg:pl-[72px]" : "lg:pl-60"}`}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/coin/:address" element={<CoinDetail />} />
            <Route path="/token/:address" element={<CoinDetail />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/my-wallet" element={<MyWallet />} />
            <Route path="/wallet-buys" element={<WalletBuys />} />
            <Route path="/launchpad" element={<Launches />} />
            <Route path="/devs" element={<Devs />} />
            <Route path="/launch-hub" element={<LaunchHub />} />
            <Route path="/fresh-drops" element={<FreshDrops />} />
            <Route path="/wallet-scout" element={<WalletScout />} />
            <Route path="/screener" element={<Screener />} />
            <Route path="/waiting-room" element={<WaitingRoom />} />
            <Route path="/confirmation" element={<Confirmation />} />
            <Route path="/funding-web" element={<FundingWeb />} />
            <Route path="/creator-coins" element={<LaunchHub />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/smart-money" element={<SmartMoney />} />
            <Route path="/clusters" element={<ClusterBuys />} />
            <Route path="/cabal" element={<CabalRadar />} />
            <Route path="/cabal/:clusterId" element={<CabalProfile />} />
            <Route path="/cto" element={<CtoRadar />} />
            <Route path="/milly" element={<MillyBoard />} />
            <Route path="/organic-runners" element={<OrganicRunners />} />
            <Route path="/candle-radar" element={<CandleRadar />} />
            <Route path="/robinhood-feed" element={<RobinhoodFeed />} />
            <Route path="/watchlist-runners" element={<WatchlistRunners />} />
            <Route path="/key" element={<Key />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/newsroom" element={<Newsroom />} />
            <Route path="/handle-radar" element={<HandleRadar />} />
            <Route path="/my-builders" element={<MyBuilders />} />
            <Route path="/proven-devs" element={<ProvenDevs />} />
            <Route path="/survivor-watch" element={<SurvivorWatch />} />
            <Route path="/github-radar" element={<GithubRadar />} />
            <Route path="/morning-call" element={<MorningCall />} />
            <Route path="/signal-scanner" element={<SignalScanner />} />
            <Route path="/momentum-radar" element={<MomentumRadar />} />            <Route path="/trending" element={<Trending />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
          </Routes>
          </ErrorBoundary>
          <footer className="max-w-[1400px] mx-auto px-6 py-10 text-center text-xs font-mono text-muted-foreground border-t border-white/5 mt-10 space-y-1.5">
            <div>ALPHAAPEING · Research tool, not financial advice · Data via DexScreener</div>
            <div className="text-muted-foreground/70">
              Wallet names are unverified on-chain heuristics — not confirmed ownership.{" "}
              <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="underline hover:text-neon transition-colors">Full disclaimer</Link>
            </div>
          </footer>
          </div>
        </BrowserRouter>
        <NetworkStatus />
        <TipBanner />
        <ReportIssueWidget />
        <Toaster theme="dark" position="bottom-right" />
        </TooltipProvider>
        </AgeFilterProvider>
        </ChainProvider>
        </HoldingsProvider>
      </WatchlistProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
